/**
 * RequireAuth — the authorisation gate, with no chrome attached to it.
 *
 * It used to be lines 51-67 of `PartnerLayout`, which meant a route could have the gate or
 * have no gate, but could not have the gate WITHOUT the header, the nav and the page
 * padding. A signing ceremony needs exactly that: a signed-in partner, on a full-screen
 * page with nothing to wander off into mid-signature.
 *
 * Used two ways:
 *   · as a layout route element, gating an `<Outlet />` (the ceremony and viewer routes);
 *   · wrapped around children (`PartnerLayout` puts its own chrome inside it).
 *
 * Behaviour is unchanged from the version inside `PartnerLayout`: spinner while auth
 * resolves, redirect to `/auth` once it has resolved unfavourably, and nothing rendered in
 * between — a child that mounts for one frame against a session that is about to be thrown
 * away is a child that fires a query as the wrong user.
 *
 * ── `isAuthorized` IS THE SESSION TEST, AND IT IS THE ONE TO GATE ON ──────────
 * It is set true on the two paths where this user resolves to a partner: the direct profile
 * read, which sets `partnerId` on the next line, and the `link_auth_profile` arm, which sets
 * `partnerId` from a SECOND profile read as `profileData?.partner_id ?? null`
 * (`src/hooks/useAuth.tsx:121-140`). So the two are not one fact spelled twice: on that
 * second arm an empty re-read leaves an AUTHORIZED session whose `partnerId` is still null.
 *
 * That is why the gate is `isAuthorized` and why every partner-keyed query in this app is
 * `enabled: !!partnerId` rather than assuming the id is there because the route rendered. The
 * agreement routes need neither — `get_my_partner_agreements`, `agreement_render` and
 * `agreement_sign` all resolve `my_partner_id()` on the server and take no id from the client.
 */

import { useEffect, type ReactNode } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';

export default function RequireAuth({ children }: { children?: ReactNode }) {
    const { user, isAuthorized, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && (!user || !isAuthorized)) {
            navigate('/auth');
        }
    }, [user, isAuthorized, isLoading, navigate]);

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!user || !isAuthorized) {
        return null;
    }

    return <>{children ?? <Outlet />}</>;
}
